import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';

import { AppLogo } from '@/assets/icons/AppLogo';
import { Icons } from '@/assets/icons/icons';
import { backendApis } from '@/backendApis';
import { ContactUsButton } from '@/components/blocks/ContactUsButton';
import {
  type BaseModalFCProps,
  type ModalFC,
} from '@/components/modals/modalsManager';
import { Button } from '@/components/ui/Button';
import { Dialog } from '@/components/ui/dialog';
import { Input } from '@/components/ui/Input';
import { XStack, YStack } from '@/components/ui/layout/layout';
import { Separator } from '@/components/ui/Separator';
import {
  integrationKeys,
  useUpdateAccountLabelMutation,
} from '@/features/integrations/integrations.query';
import { handleOpenContactsSettings } from '@/integrations/integration-handlers';
import { getIntegrationIcon } from '@/integrations/integration-icons';
import { handleExternalUrlClick } from '@/utils/platform';

type ConnectionStep = 'idle' | 'polling' | 'connected' | 'error';

function ConnectionIconRow({
  step,
  toolkitName,
}: {
  step: ConnectionStep;
  toolkitName: string;
}) {
  const IntegrationIcon = getIntegrationIcon(toolkitName);

  if (step === 'connected') {
    return (
      <XStack center className="pt-6 pb-3">
        <XStack
          center
          className="relative size-12 rounded-xl bg-surface-subtle border border-border"
        >
          <IntegrationIcon className="size-8" />
          <XStack
            center
            className="absolute -bottom-1 -right-1 size-4 rounded-full bg-green-500"
          >
            <Icons.check className="size-2.5 text-white" />
          </XStack>
        </XStack>
      </XStack>
    );
  }

  if (step === 'error') {
    return (
      <XStack center className="pt-6 pb-3">
        <XStack
          center
          className="relative size-12 rounded-xl bg-surface-subtle border border-border"
        >
          <IntegrationIcon className="size-8" />
          <XStack
            center
            className="absolute -bottom-1 -right-1 size-4 rounded-full bg-danger-9"
          >
            <Icons.cancel className="size-2.5 text-white" />
          </XStack>
        </XStack>
      </XStack>
    );
  }

  return (
    <XStack center className="gap-3 pt-6 pb-3">
      <XStack
        center
        className="size-12 rounded-xl bg-surface-subtle border border-border"
      >
        <AppLogo className="size-8" />
      </XStack>
      <XStack center className="gap-1">
        {step === 'polling' ? (
          <>
            <div
              className="size-1 rounded-full bg-foreground-muted animate-bounce"
              style={{ animationDelay: '0ms', animationDuration: '0.6s' }}
            />
            <div
              className="size-1 rounded-full bg-foreground-muted animate-bounce"
              style={{ animationDelay: '100ms', animationDuration: '0.6s' }}
            />
            <div
              className="size-1 rounded-full bg-foreground-muted animate-bounce"
              style={{ animationDelay: '200ms', animationDuration: '0.6s' }}
            />
          </>
        ) : (
          <>
            <div className="size-1 rounded-full bg-foreground-muted" />
            <div className="size-1 rounded-full bg-foreground-muted" />
            <div className="size-1 rounded-full bg-foreground-muted" />
          </>
        )}
      </XStack>
      <XStack
        center
        className="size-12 rounded-xl bg-surface-subtle border border-border"
      >
        <IntegrationIcon className="size-8" />
      </XStack>
    </XStack>
  );
}

export interface IntegrationConnectionModalProps extends BaseModalFCProps {
  redirectUrl: string;
  toolkitName: string;
  displayName: string;
  onComplete?: (success: boolean) => void;
  accountId?: string | null;
  flowId?: string | null;
  accountLabel?: string | null;
}

export const IntegrationConnectionModal: ModalFC<
  IntegrationConnectionModalProps
> = ({
  open,
  onOpenChange,
  redirectUrl,
  toolkitName,
  displayName,
  onComplete,
  accountId,
  flowId,
  accountLabel,
}) => {
  const queryClient = useQueryClient();
  const updateLabelMutation = useUpdateAccountLabelMutation();
  const [pollingState, setPollingState] = useState<
    'idle' | 'polling' | 'success' | 'error'
  >('idle');
  const [errorReason, setErrorReason] = useState<'failed' | 'timeout'>(
    'failed'
  );
  const [connectedAccountId, setConnectedAccountId] = useState<string | null>(
    null
  );
  const [customLabel, setCustomLabel] = useState('');

  const [otherLabels, setOtherLabels] = useState<string[]>([]);
  const pollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const attemptCountRef = useRef(0);
  const isProcessingRef = useRef(false);
  const hasCompletedRef = useRef(false);
  const resolvedAccountIdRef = useRef<string | null>(accountId ?? null); // For chat, when we only have an auth URL and need to find the pending account
  const MAX_ATTEMPTS = 60; // 5 minutes at 5-second intervals

  const cleanupPolling = useCallback(() => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
    attemptCountRef.current = 0;
    isProcessingRef.current = false;
  }, []);

  const deletePendingAccount = useCallback(() => {
    if (resolvedAccountIdRef.current) {
      backendApis.integrations
        .disconnectIntegration(resolvedAccountIdRef.current)
        .catch(() => {});
    }
  }, []);

  // User abandoned the OAuth flow (closed the popup/modal). Cancel the in-progress
  // account via the flow-scoped endpoint so it doesn't linger in 'pending'.
  // Only act when we hold a flow token: cancelPendingOAuth is non-destructive — the
  // backend no-ops it unless the row is still PENDING for this exact flow, so it
  // can't tear down an account that just connected in the race between approval and
  // the next poll. Without a flow_id (e.g. the chat flow resolves the account by
  // lookup) we can't prove the id is a row this modal created, so we must NOT
  // disconnect it; the backend's PENDING-timeout reaper cancels genuinely abandoned
  // rows.
  const abandonPendingAccount = useCallback(() => {
    const id = resolvedAccountIdRef.current;
    if (id && flowId) {
      backendApis.integrations.cancelPendingOAuth(id, flowId).catch(() => {});
    }
  }, [flowId]);

  const startPolling = useCallback(() => {
    setPollingState('polling');
    attemptCountRef.current = 0;

    pollIntervalRef.current = setInterval(async () => {
      if (isProcessingRef.current) {
        return;
      }

      attemptCountRef.current++;

      try {
        const accounts =
          await backendApis.integrations.getConnectedAccounts(toolkitName);
        if (!resolvedAccountIdRef.current) {
          const pending = [...accounts]
            .reverse()
            .find(
              (acc) =>
                acc.connection_status === 'pending' &&
                acc.toolkit_name === toolkitName &&
                acc.account_label === accountLabel
            );
          if (pending) {
            resolvedAccountIdRef.current = pending.id;
          }
        }
        const account = resolvedAccountIdRef.current
          ? accounts.find((acc) => acc.id === resolvedAccountIdRef.current)
          : undefined;

        if (account?.connection_status === 'connected') {
          isProcessingRef.current = true;
          cleanupPolling();

          const currentLabel = account.account_label?.trim() || '';

          setPollingState('success');
          setConnectedAccountId(account.id);
          setCustomLabel(currentLabel);

          const others = accounts
            .filter(
              (acc) =>
                acc.id !== account.id &&
                acc.toolkit_name === toolkitName &&
                acc.connection_status === 'connected' &&
                acc.account_label
            )
            .map((acc) => acc.account_label);
          setOtherLabels(others);

          queryClient.invalidateQueries({
            queryKey: integrationKeys.all,
          });

          hasCompletedRef.current = true;
          onComplete?.(true);
        } else if (account?.connection_status === 'failed') {
          isProcessingRef.current = true;
          hasCompletedRef.current = true;
          setErrorReason('failed');
          setPollingState('error');
          cleanupPolling();
          deletePendingAccount();
          onComplete?.(false);
        } else {
          if (attemptCountRef.current >= MAX_ATTEMPTS) {
            isProcessingRef.current = true;
            hasCompletedRef.current = true;
            setErrorReason('timeout');
            setPollingState('error');
            cleanupPolling();
            deletePendingAccount();
            onComplete?.(false);
          }
        }
      } catch (error) {
        console.error('Polling error:', error);
        if (attemptCountRef.current >= MAX_ATTEMPTS) {
          isProcessingRef.current = true;
          hasCompletedRef.current = true;
          setErrorReason('failed');
          setPollingState('error');
          cleanupPolling();
          deletePendingAccount();
          onComplete?.(false);
        }
      }
    }, 5000);
  }, [
    toolkitName,
    cleanupPolling,
    deletePendingAccount,
    onComplete,
    queryClient,
    accountLabel,
  ]);

  const handleContinue = () => {
    handleExternalUrlClick(redirectUrl, { forceInBrowser: true });
    startPolling();
  };

  const handleDone = async () => {
    const trimmedLabel = customLabel.trim();
    if (connectedAccountId && trimmedLabel) {
      try {
        await updateLabelMutation.mutateAsync({
          accountId: connectedAccountId,
          newLabel: trimmedLabel,
        });
      } catch {
        toast.error('Failed to update label');
      }
    }
    onOpenChange(false);
  };

  useEffect(() => {
    return () => {
      cleanupPolling();
    };
  }, [cleanupPolling]);

  const handleOpenChange = (isOpen: boolean) => {
    if (!isOpen && !hasCompletedRef.current) {
      // Closing the modal (X / Esc / click-outside) before the flow resolves
      // abandons it. A PENDING account already exists (connect runs before the
      // modal opens), so cancel it instead of leaving it stuck in 'pending'.
      hasCompletedRef.current = true;
      cleanupPolling();
      abandonPendingAccount();
      onComplete?.(false);
    }
    onOpenChange(isOpen);
  };

  return (
    <Dialog.Root open={open} onOpenChange={handleOpenChange}>
      <Dialog.Content className="max-w-md gap-4 !z-[100] no-drag">
        <Dialog.Header>
          <Dialog.Title className="text-lg">
            {pollingState === 'polling'
              ? `Connecting to ${displayName}`
              : `Connect ${displayName}`}
          </Dialog.Title>
        </Dialog.Header>

        {pollingState === 'polling' ? (
          <>
            <ConnectionIconRow step="polling" toolkitName={toolkitName} />

            <YStack center className="gap-1 text-center text-balance">
              <p className="font-medium text-foreground">
                Waiting for connection...
              </p>
              <p className="text-foreground-muted leading-snug">
                Please complete the authentication in the opened window
              </p>
            </YStack>

            <Button
              variant="secondary"
              className="w-full mt-4"
              onClick={() => {
                cleanupPolling();
                abandonPendingAccount();
                hasCompletedRef.current = true;
                onComplete?.(false);
                onOpenChange(false);
              }}
            >
              Cancel
            </Button>
          </>
        ) : pollingState === 'success' ? (
          otherLabels.length > 0 ? (
            <YStack center className="gap-6">
              <ConnectionIconRow step="connected" toolkitName={toolkitName} />
              <YStack center className="gap-1 text-center text-balance">
                <p className="font-medium text-foreground">
                  {displayName} connected successfully!
                </p>
                <p className="text-foreground-muted leading-snug">
                  Give this account a label so Littlebird can tell it apart.
                </p>
              </YStack>
              <Separator />
              <YStack className="w-full gap-2">
                <label
                  htmlFor="account-label"
                  className="text-sm font-medium text-foreground"
                >
                  Account label
                </label>
                <Input
                  id="account-label"
                  value={customLabel}
                  onChange={(e) => setCustomLabel(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleDone();
                  }}
                  placeholder="e.g., Work, Personal"
                  className="text-sm"
                  autoFocus
                />
                <p className="text-xs text-foreground-muted">
                  Existing accounts: {otherLabels.join(', ')}
                </p>
              </YStack>
              <Button
                onClick={handleDone}
                disabled={!customLabel.trim() || updateLabelMutation.isPending}
                className="w-full"
                size="lg"
              >
                Done
              </Button>
            </YStack>
          ) : (
            <>
              <ConnectionIconRow step="connected" toolkitName={toolkitName} />
              <YStack center className="gap-1 text-center text-balance">
                <p className="font-medium text-foreground">
                  {displayName} connected successfully!
                </p>
                <p className="text-foreground-muted leading-snug">
                  You can now use {displayName} in your conversations.
                </p>
              </YStack>
              <Button
                className="w-full mt-4"
                size="lg"
                onClick={() => onOpenChange(false)}
              >
                Done
              </Button>
            </>
          )
        ) : pollingState === 'error' ? (
          <>
            <ConnectionIconRow step="error" toolkitName={toolkitName} />
            <YStack center className="gap-1 text-center text-balance">
              <p className="font-medium text-foreground">
                {errorReason === 'timeout'
                  ? 'Connection timed out'
                  : 'Connection failed'}
              </p>
              <p className="text-foreground-muted leading-snug">
                {errorReason === 'timeout'
                  ? `We didn't hear back from ${displayName} in time. The sign-in window may have been closed before completing.`
                  : `Something went wrong while connecting to ${displayName}.`}
              </p>
            </YStack>
            <XStack className="gap-3 mt-4">
              <ContactUsButton
                className="w-full"
                size="lg"
                variant="outline"
                subject={`Issue connecting ${displayName}`}
              />
              <Button
                className="w-full"
                size="lg"
                onClick={() => {
                  setPollingState('idle');
                  hasCompletedRef.current = false;
                  resolvedAccountIdRef.current = null;
                }}
              >
                Try again
              </Button>
            </XStack>
          </>
        ) : (
          <>
            <ConnectionIconRow step="idle" toolkitName={toolkitName} />

            <YStack className="gap-3">
              <XStack className="gap-3 items-start">
                <div className="flex items-center justify-center size-6 shrink-0 mt-0.5">
                  <Icons.externalLink className="size-4 text-foreground-subtle" />
                </div>
                <p className="text-sm text-foreground-muted leading-snug">
                  You'll sign in on {displayName}'s page and return here when
                  done.
                </p>
              </XStack>

              <XStack className="gap-3 items-start">
                <div className="flex items-center justify-center size-6 shrink-0 mt-0.5">
                  <Icons.requestPermission className="size-4 text-foreground-subtle" />
                </div>
                <p className="text-sm text-foreground-muted leading-snug">
                  Your data is used only to answer your questions. You can
                  revoke access anytime.{' '}
                  <span
                    className="text-foreground underline cursor-pointer"
                    onClick={() =>
                      handleExternalUrlClick(
                        'https://littlebird.ai/privacy-policy'
                      )
                    }
                  >
                    Privacy policy
                  </span>
                </p>
              </XStack>
            </YStack>

            <Dialog.Footer className="mt-1">
              <Button onClick={handleContinue} className="w-full" size="lg">
                Continue to {displayName}
              </Button>
            </Dialog.Footer>
          </>
        )}
      </Dialog.Content>
    </Dialog.Root>
  );
};

export interface EventKitPermissionModalProps extends BaseModalFCProps {
  type: 'calendar' | 'reminders';
}

export const EventKitPermissionModal: ModalFC<EventKitPermissionModalProps> = ({
  open,
  onOpenChange,
  type,
}) => {
  const handleOpenSettings = async () => {
    try {
      await window.mainProcessApi.openEventKitSettings(type);
      onOpenChange(false);
    } catch (error) {
      console.error('Failed to open system settings:', error);
      toast.error('Failed to open System Settings');
    }
  };

  const title = type === 'calendar' ? 'Calendar' : 'Reminders';

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Content className="max-w-md">
        <Dialog.Header>
          <Dialog.Title>{title} Permission Required</Dialog.Title>
        </Dialog.Header>

        <YStack className="gap-4 py-4">
          <p className="text-sm text-muted-foreground">
            {title} permission was denied. To grant access, go to System
            Settings under{' '}
            <span className="font-medium">Privacy & Security → {title}</span>{' '}
            and enable Littlebird.
          </p>
        </YStack>

        <Dialog.Footer>
          <Button variant="secondary" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleOpenSettings}>Open System Settings</Button>
        </Dialog.Footer>
      </Dialog.Content>
    </Dialog.Root>
  );
};

export const ContactsPermissionModal: ModalFC<BaseModalFCProps> = ({
  open,
  onOpenChange,
}) => {
  const handleOpenSettings = async () => {
    try {
      await handleOpenContactsSettings();
      onOpenChange(false);
    } catch (error) {
      console.error('Failed to open system settings:', error);
      toast.error('Failed to open System Settings');
    }
  };

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Content className="max-w-md">
        <Dialog.Header>
          <Dialog.Title>Contacts Permission Required</Dialog.Title>
        </Dialog.Header>

        <YStack className="gap-4 py-4">
          <p className="text-sm text-muted-foreground">
            Contacts permission was denied. To grant access, go to System
            Settings under{' '}
            <span className="font-medium">Privacy & Security → Contacts</span>{' '}
            and enable Littlebird.
          </p>
        </YStack>

        <Dialog.Footer>
          <Button variant="secondary" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleOpenSettings}>Open System Settings</Button>
        </Dialog.Footer>
      </Dialog.Content>
    </Dialog.Root>
  );
};
